import { useQuery } from 'react-query';
import { apiClient } from '../services/api';
import type { AuditLog, EntityType } from '../types';

/**
 * Generic hook to fetch audit logs for any entity
 */
export const useAuditLog = (entityType: EntityType, entityId: string) => {
  return useQuery(
    ['audit', entityType, entityId],
    async () => {
      // Map entity types to API endpoints
      const endpointMap: Record<EntityType, string> = {
        PROJECT: `/api/v1/audit/projects/${entityId}`,
        EXPENSE: `/api/v1/audit/expenses/${entityId}`,
        INVOICE: `/api/v1/audit/invoices/${entityId}`,
        CATEGORY: `/api/v1/audit/categories/${entityId}`,
        SUBCATEGORY: `/api/v1/audit/subcategories/${entityId}`,
        USER: `/api/v1/audit/users/${entityId}`,
        COMPANY: `/api/v1/audit/companies/${entityId}`,
        ASSET: `/api/v1/audit/assets/${entityId}`,
        ASSET_TYPE: `/api/v1/audit/asset-types/${entityId}`,
        ATTACHMENT: `/api/v1/audit/attachments/${entityId}`,
        AUDIT_LOG: `/api/v1/audit/${entityId}`,
        CLIENT: `/api/v1/audit/clients/${entityId}`,
      };

      const endpoint = endpointMap[entityType];
      if (!endpoint) {
        throw new Error(`No audit endpoint configured for entity type: ${entityType}`);
      }

      const response = await apiClient.get<AuditLog[]>(endpoint);
      
      return response.data;
    },
    {
      enabled: !!entityId,
      refetchOnMount: 'always',
      refetchOnWindowFocus: true,
      cacheTime: 0,
      staleTime: 0,
    }
  );
};

/**
 * Legacy hook for backward compatibility
 */
export const useProjectAuditLog = (projectId: string) => {
  return useAuditLog('PROJECT', projectId);
};
